<template>
  <div
    class="cve-attack-visualizer"
    :class="{ 'fullscreen-mode': isFullscreen && !isEmbedded, 'embedded-mode': isEmbedded, 'dark-theme': isDark }"
  >
    <!-- Fullscreen Overlay (only when not embedded) -->
    <div v-if="isFullscreen && !isEmbedded" class="fullscreen-overlay">
      <div class="fullscreen-container">
        <div class="fullscreen-header">
          <div class="fullscreen-header__left">
            <div class="visualizer-title">
              {{ $t('domain.insights.cve.attack.title') }}
            </div>
            <div class="visualizer-subtitle">
              {{ dynamicSubtitle }}
            </div>
          </div>
          <div class="fullscreen-header__controls">
            <!-- CVE Selector -->
            <v-menu
              v-model="fullscreenMenuOpen"
              :close-on-content-click="false"
              offset-y
              max-width="320"
              min-width="280"
            >
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="cve-selector-wrapper">
                  <v-text-field
                    :value="selectorDisplayText"
                    readonly
                    dense
                    outlined
                    hide-details
                    class="cve-selector-input"
                    append-icon="mdi-chevron-down"
                  />
                </div>
              </template>
              <v-card class="cve-dropdown">
                <div class="cve-dropdown__search-wrapper">
                  <v-icon small color="grey">mdi-magnify</v-icon>
                  <input
                    v-model="cveSearchQuery"
                    type="text"
                    :placeholder="$t('domain.insights.cve.attack.search.placeholder')"
                    class="cve-dropdown__search-input"
                  />
                  <v-icon
                    v-if="cveSearchQuery"
                    small
                    color="grey"
                    class="cve-dropdown__clear-search"
                    @click="cveSearchQuery = ''"
                  >
                    mdi-close
                  </v-icon>
                </div>
                <div class="cve-dropdown__actions">
                  <v-btn x-small text color="primary" @click="selectAllCves">
                    <v-icon x-small left>mdi-checkbox-multiple-marked</v-icon>
                    {{ $t('domain.insights.cve.attack.select.all') }}
                  </v-btn>
                  <v-btn x-small text color="error" @click="clearSelection">
                    <v-icon x-small left>mdi-close-circle</v-icon>
                    {{ $t('domain.insights.cve.attack.clear') }}
                  </v-btn>
                </div>
                <v-divider />
                <v-list dense class="cve-dropdown__list">
                  <v-list-item
                    v-for="cve in searchFilteredCves"
                    :key="cve"
                    class="cve-dropdown__item"
                    @click="toggleCve(cve)"
                  >
                    <v-list-item-action class="mr-2">
                      <v-checkbox
                        :input-value="selectedCves.includes(cve)"
                        color="primary"
                        dense
                        hide-details
                        @click.stop="toggleCve(cve)"
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title class="cve-dropdown__cve-text">
                        {{ cve }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <v-divider />
                <div class="cve-dropdown__footer">
                  <span class="cve-dropdown__count">
                    {{ selectedCves.length }} / {{ filteredCvesList.length }} selected
                  </span>
                  <v-btn small color="primary" depressed @click="fullscreenMenuOpen = false">
                    Done
                  </v-btn>
                </div>
              </v-card>
            </v-menu>

            <!-- Filters -->
            <c-v-e-filters-menu
              :value="filters"
              :apt-groups-list="aptGroupsList"
              @input="filters = $event"
            />

            <!-- Export Menu -->
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  outlined
                  :disabled="!hasData"
                  class="export-btn"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small left>mdi-download</v-icon>
                  Export
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="exportData('json')">
                  <v-list-item-icon class="mr-2">
                    <v-icon small>mdi-code-json</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Export as JSON</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Exit Fullscreen Button -->
            <v-btn
              small
              outlined
              class="exit-fullscreen-btn"
              @click="closeFullscreen"
            >
              <v-icon small>mdi-fullscreen-exit</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="fullscreen-content">
          <div ref="fullscreenSankeyContainer" class="fullscreen-visualization" />
          <transition name="slide-panel">
            <resizable-panel
              v-if="selectedPathInfo"
              class="info-panel fullscreen-info-panel"
              :initial-width="550"
              :min-width="450"
              :max-width-percent="80"
              position="left"
              :is-dark="isDark"
              storage-key="cve-visualizer-panel-width-fullscreen"
            >
              <c-v-e-info-panel
                :info="selectedPathInfo"
                :attack-paths-map="attackPathsMap"
                :apt-groups="computedAptGroups"
                :is-dark="isDark"
                @close="clearPathSelection"
              />
            </resizable-panel>
          </transition>
        </div>
      </div>
    </div>

    <!-- Header Section -->
    <c-v-e-visualizer-header
      v-if="!hideHeader"
      :available-cves="filteredCvesList"
      :selected-cves="selectedCves"
      :filters="filters"
      :apt-groups-list="aptGroupsList"
      :has-data="hasData"
      :search-query="cveSearchQuery"
      :path-mode="pathMode"
      @update:filters="filters = $event"
      @update:searchQuery="cveSearchQuery = $event"
      @update:pathMode="updatePathMode"
      @select-all="selectAllCves"
      @clear-selection="clearSelection"
      @toggle-cve="toggleCve"
      @export="exportData($event)"
      @fullscreen="openFullscreen"
    />

    <!-- Loading Overlay - fixed position, below sidebar -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-content">
        <v-progress-circular indeterminate color="primary" size="48" />
        <span class="loading-text">{{ $t('domain.insights.cve.attack.processing') }}</span>
      </div>
    </div>

    <!-- No CVEs Available -->
    <div v-if="!loading && availableCves.length === 0" class="visualizer-no-data">
      <v-icon large color="grey lighten-1">mdi-alert-circle-outline</v-icon>
      <div class="no-data-text">{{ $t('domain.insights.cve.attack.no.cves') }}</div>
    </div>

    <!-- Visualization Area -->
    <div v-else-if="availableCves.length > 0" class="visualization-area">
      <div v-if="!hasData && !hasProcessed && !loading" class="visualizer-empty">
        <v-icon large color="grey lighten-1">mdi-chart-sankey-variant</v-icon>
        <div class="empty-text">Select CVEs to see the attack flow visualization</div>
      </div>

      <div v-else-if="!hasData && hasProcessed && !loading" class="visualizer-no-data">
        <v-icon large color="grey lighten-1">mdi-alert-circle-outline</v-icon>
        <div class="no-data-text">{{ $t('domain.insights.cve.attack.no.mapping') }}</div>
      </div>

      <!-- Main Content with Info Panel -->
      <div v-else-if="hasData" class="visualizer-content-wrapper">
        <div class="visualizer-content" :class="{ 'with-panel': selectedPathInfo }">
          <div ref="sankeyContainer" class="sankey-container" />
        </div>
        <transition name="slide-panel">
          <resizable-panel
            v-if="selectedPathInfo"
            class="info-panel"
            :initial-width="550"
            :min-width="450"
            :max-width-percent="80"
            position="left"
            :is-dark="isDark"
            storage-key="cve-visualizer-panel-width"
          >
            <c-v-e-info-panel
              :info="selectedPathInfo"
              :attack-paths-map="attackPathsMap"
              :apt-groups="computedAptGroups"
              :is-dark="isDark"
              @close="clearPathSelection"
            />
          </resizable-panel>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import CVEInfoPanel from './CVEInfoPanel.vue';
import CVEVisualizerHeader from './CVEVisualizerHeader.vue';
import CVEFiltersMenu from './CVEFiltersMenu.vue';
import ResizablePanel from '../ResizablePanel.vue';
import {
  LAYOUT_CONFIG,
  getNodeColor,
  extractCvesFromInsights,
  buildAttackPathsMap,
  buildAptGroupTtpsMap,
  createSankeyGenerator,
  generateGradientLinkPath,
  updateLinkPositions,
  getConnectedElements,
  reassignNodesToLogicalColumns,
} from './cveVisualizerUtils';
import {
  generateHtmlExport,
  prepareSvgForExport,
  downloadFile,
} from './exportHtmlTemplate';

export default {
  name: 'CVEAttackVisualizer',
  components: {
    CVEInfoPanel,
    CVEVisualizerHeader,
    CVEFiltersMenu,
    ResizablePanel,
  },
  props: {
    insightsData: {
      type: Object,
      default: null,
    },
    isEmbedded: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    externalSelectedCves: {
      type: Array,
      default: null,
    },
    isDark: {
      type: Boolean,
      default: false,
    },
    flowSettings: {
      type: Object,
      default: () => ({
        nodeWidth: 10,
        nodePadding: 16,
        linkOpacity: 35,
        labelSize: 11,
      }),
    },
    assetName: {
      type: String,
      default: null,
    },
    assetCorrelation: {
      type: Object,
      default: () => ({}),
    },
    filteredVisualization: {
      type: Object,
      default: null,
    },
    pathMode: {
      type: String,
      default: 'full',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      hasProcessed: false,
      isFullscreen: false,
      fullscreenMenuOpen: false,
      chartNodes: [],
      chartLinks: [],
      selectedCves: [],
      cveSearchQuery: '',
      debounceTimer: null,
      filterDebounceTimer: null,
      selectedPath: null,
      selectedPathInfo: null,
      filters: { aptGroups: [] },
      cachedFilteredCves: null,
      nodePositions: {},
      currentZoomTransform: null,
      internalPathMode: 'full',
      isAnimating: false,
      hasRenderedOnce: false,
    };
  },
  computed: {
    hasData() {
      return this.chartNodes.length > 0;
    },
    availableCves() {
      return extractCvesFromInsights(this.insightsData);
    },
    computedAptGroups() {
      // Check multiple possible locations for APT groups
      const groups = this.insightsData?.groups;
      if (groups && Array.isArray(groups) && groups.length > 0) return groups;

      const aptGroups = this.insightsData?.insights?.relevant_APT_groups;
      if (aptGroups && aptGroups !== 'N/A' && Array.isArray(aptGroups) && aptGroups.length > 0) {
        return aptGroups;
      }

      // Also check organization level
      const orgAptGroups = this.insightsData?.organization?.relevant_APT_groups;
      if (orgAptGroups && orgAptGroups !== 'N/A' && Array.isArray(orgAptGroups)) {
        return orgAptGroups;
      }

      return [];
    },
    aptGroupsList() {
      return this.computedAptGroups.map(g => g.name || g['APT name'] || g.APT_group).filter(Boolean);
    },
    attackPathsMap() {
      return buildAttackPathsMap(this.insightsData);
    },
    aptGroupTtpsMap() {
      return buildAptGroupTtpsMap(this.computedAptGroups);
    },
    hasActiveFilters() {
      return this.filters.aptGroups && this.filters.aptGroups.length > 0;
    },
    filteredCvesList() {
      if (!this.hasActiveFilters) return this.availableCves;
      if (this.cachedFilteredCves !== null) return this.cachedFilteredCves;
      return this.applyFilters(this.availableCves);
    },
    selectorDisplayText() {
      if (this.selectedCves.length === 0) return 'Select CVEs...';
      if (this.selectedCves.length === 1) return this.selectedCves[0];
      return `${this.selectedCves.length} CVEs selected`;
    },
    searchFilteredCves() {
      if (!this.cveSearchQuery) return this.filteredCvesList;
      const query = this.cveSearchQuery.toLowerCase();
      return this.filteredCvesList.filter(cve => cve.toLowerCase().includes(query));
    },
    effectiveAssetName() {
      // Use prop if provided, otherwise try to extract from data
      if (this.assetName) return this.assetName;

      // Check if insightsData has organization.name
      if (this.insightsData?.organization?.name) {
        return this.insightsData.organization.name;
      }

      // Try to find the domain key from the parent structure
      if (this.insightsData) {
        const keys = Object.keys(this.insightsData);
        const domainKey = keys.find(k => k.includes('.') && !k.startsWith('_'));
        if (domainKey) return domainKey;
      }

      return null;
    },
    effectiveAssetCorrelation() {
      // Use prop if provided, otherwise extract from insightsData
      if (this.assetCorrelation && Object.keys(this.assetCorrelation).length > 0) {
        return this.assetCorrelation;
      }
      // Fallback: extract from insightsData
      return this.insightsData?.assetCorrelation || {};
    },
    effectivePathMode() {
      // Use prop if provided (from parent), otherwise use internal state
      return this.pathMode || this.internalPathMode;
    },
    dynamicSubtitle() {
      const pathModeSubtitles = {
        full: 'Domain → Asset → Tech → CVE → CWE → CAPEC → TTP → APT',
        attack: 'CVE → CWE → CAPEC → TTP → APT',
        threat: 'CVE → TTP → APT',
        direct: 'CVE → APT',
      };
      return pathModeSubtitles[this.effectivePathMode] || pathModeSubtitles.full;
    },
  },
  watch: {
    pathMode: {
      handler(newVal) {
        if (newVal && newVal !== this.internalPathMode) {
          this.internalPathMode = newVal;
          // Re-process CVEs with new path mode
          if (this.selectedCves.length > 0) {
            this.processCves(this.selectedCves);
          }
        }
      },
      immediate: true,
    },
    selectedCves: {
      handler(newVal) {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          if (newVal.length > 0) {
            this.processCves(newVal);
          } else {
            this.chartNodes = [];
            this.chartLinks = [];
            this.hasProcessed = false;
            this.selectedPathInfo = null;
          }
        }, 500);
      },
      deep: true,
    },
    filters: {
      handler() {
        if (this.filterDebounceTimer) clearTimeout(this.filterDebounceTimer);
        this.filterDebounceTimer = setTimeout(() => {
          this.cachedFilteredCves = this.applyFilters(this.availableCves);
          if (this.selectedCves.length > 0) {
            const filtered = this.applyFilters(this.selectedCves);
            if (filtered.length > 0) {
              this.processCves(filtered);
            } else {
              this.chartNodes = [];
              this.chartLinks = [];
              this.selectedPathInfo = null;
            }
          }
        }, 300);
      },
      deep: true,
    },
    availableCves() {
      this.cachedFilteredCves = null;
    },

    externalSelectedCves: {
      handler(newVal) {
        if (newVal !== null) {
          this.selectedCves = [...newVal];
        }
      },
      immediate: true,
      deep: true,
    },
    isDark() {
      // Re-render chart when theme changes
      if (this.hasData) {
        this.$nextTick(() => this.renderSankeyChart());
      }
    },
    'flowSettings.nodeWidth': function flowSettingsNodeWidth() {
      if (this.hasData) this.$nextTick(() => this.renderSankeyChart());
    },
    'flowSettings.nodePadding': function flowSettingsNodePadding() {
      if (this.hasData) this.$nextTick(() => this.renderSankeyChart());
    },
    'flowSettings.linkOpacity': function flowSettingsLinkOpacity() {
      if (this.hasData) this.$nextTick(() => this.renderSankeyChart());
    },
    'flowSettings.labelSize': function flowSettingsLabelSize() {
      if (this.hasData) this.$nextTick(() => this.renderSankeyChart());
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.filterDebounceTimer) clearTimeout(this.filterDebounceTimer);
  },
  methods: {
    updateSettings() {
      // Method called from parent to update settings
      // The watcher will handle re-rendering
      this.$nextTick(() => this.renderSankeyChart());
    },
    applyFilters(cvesList) {
      if (!this.filters.aptGroups || this.filters.aptGroups.length === 0) return cvesList;
      return cvesList.filter(cve => {
        const path = this.attackPathsMap[cve];
        if (!path?.techniques) return false;
        return this.filters.aptGroups.some(groupName => {
          const groupTtps = this.aptGroupTtpsMap[groupName];
          if (!groupTtps) return false;
          return path.techniques.some(tech => groupTtps.has(tech));
        });
      });
    },
    getNodeType(name) {
      const nameLower = name.toLowerCase();
      if (nameLower.startsWith('org:')) return 'org';
      if (nameLower.startsWith('asset:')) return 'asset';
      if (nameLower.startsWith('tech:')) return 'tech';
      if (name.startsWith('CVE-') || name.startsWith('EXPOSED-') || name.startsWith('ASSET-')) {
        return 'cve';
      }
      if (name.startsWith('CWE-')) return 'cwe';
      if (name.startsWith('CAPEC-')) return 'capec';
      if (/^T\d+/.test(name)) return 'technique';
      return 'unknown';
    },
    handleKeydown(e) {
      if (e.key === 'Escape' && this.isFullscreen) this.closeFullscreen();
    },
    handleResize() {
      if (this.hasData && this.chartLinks.length > 0) this.renderSankeyChart();
    },
    selectAllCves() {
      this.selectedCves = [...this.filteredCvesList];
    },
    clearSelection() {
      this.selectedCves = [];
      this.chartNodes = [];
      this.chartLinks = [];
      this.hasProcessed = false;
      this.selectedPathInfo = null;
    },
    toggleCve(cve) {
      const idx = this.selectedCves.indexOf(cve);
      if (idx > -1) this.selectedCves.splice(idx, 1);
      else this.selectedCves.push(cve);
    },
    clearPathSelection() {
      this.selectedPath = null;
      this.selectedPathInfo = null;
      this.$nextTick(() => this.renderSankeyChart());
    },
    clearSavedPositions() {
      // Clear saved node positions and zoom transform
      // This is called when switching from graph view to flow view
      this.nodePositions = {};
      this.currentZoomTransform = null;
    },
    updatePathMode(mode) {
      this.internalPathMode = mode;
      // Re-process CVEs with new path mode
      if (this.selectedCves.length > 0) {
        this.processCves(this.selectedCves);
      }
    },
    /**
     * Filter nodes and links based on path mode
     * - full: All columns (Asset → Tech → CVE → CWE → CAPEC → TTP → APT)
     * - attack: CVE → CWE → CAPEC → TTP → APT (hide Asset/Tech)
     * - threat: CVE → TTP → APT (hide CWE/CAPEC)
     * - direct: CVE → APT (minimal view)
     */
    applyPathModeFilter(nodes, links) {
      const mode = this.effectivePathMode;
      if (mode === 'full') {
        return { nodes, links };
      }

      // Define which node types to keep for each mode
      const modeConfig = {
        attack: { keep: ['cve', 'cwe', 'capec', 'ttp', 'apt'], collapse: ['org', 'asset', 'tech'] },
        threat: { keep: ['cve', 'ttp', 'apt'], collapse: ['org', 'asset', 'tech', 'cwe', 'capec'] },
        direct: { keep: ['cve', 'apt'], collapse: ['org', 'asset', 'tech', 'cwe', 'capec', 'ttp'] },
      };

      const config = modeConfig[mode];
      if (!config) return { nodes, links };

      // Build node type map
      const nodeTypeMap = new Map();
      nodes.forEach(n => {
        const type = this.getNodeTypeFromId(n.name);
        nodeTypeMap.set(n.name, type);
      });

      // Filter nodes - keep only those in the keep list
      const filteredNodes = nodes.filter(n => {
        const type = nodeTypeMap.get(n.name);
        return config.keep.includes(type);
      });
      const filteredNodeSet = new Set(filteredNodes.map(n => n.name));

      // Build adjacency for path finding
      const forwardLinks = new Map(); // source -> [targets]
      const backwardLinks = new Map(); // target -> [sources]
      links.forEach(l => {
        if (!forwardLinks.has(l.source)) forwardLinks.set(l.source, []);
        forwardLinks.get(l.source).push(l.target);
        if (!backwardLinks.has(l.target)) backwardLinks.set(l.target, []);
        backwardLinks.get(l.target).push(l.source);
      });

      // Create new links that skip collapsed nodes
      const newLinks = [];
      const linkSet = new Set();

      // For each kept node, find connections to other kept nodes
      filteredNodes.forEach(node => {
        // Find forward connections (skip collapsed nodes)
        const findForwardConnections = (startNode, visited = new Set()) => {
          const targets = forwardLinks.get(startNode) || [];
          targets.forEach(target => {
            if (visited.has(target)) return;
            visited.add(target);

            if (filteredNodeSet.has(target)) {
              // Direct connection to a kept node
              const linkKey = `${node.name}|${target}`;
              if (!linkSet.has(linkKey)) {
                linkSet.add(linkKey);
                newLinks.push({ source: node.name, target, value: 1 });
              }
            } else {
              // Collapsed node - continue searching
              findForwardConnections(target, visited);
            }
          });
        };

        findForwardConnections(node.name);
      });

      return { nodes: filteredNodes, links: newLinks };
    },
    getNodeTypeFromId(id) {
      const idLower = id.toLowerCase();
      if (idLower.startsWith('org:')) return 'org';
      if (idLower.startsWith('asset:')) return 'asset';
      if (idLower.startsWith('tech:')) return 'tech';
      if (id.startsWith('CVE-')) return 'cve';
      if (id.startsWith('CWE-')) return 'cwe';
      if (id.startsWith('CAPEC-')) return 'capec';
      if (idLower.startsWith('apt:')) return 'apt';
      if (/^T\d+/.test(id)) return 'ttp';
      return 'unknown';
    },
    saveNodePositions() {
      // Store absolute positions from the current D3 data bound to nodes
      const container = this.isFullscreen
        ? this.$refs.fullscreenSankeyContainer
        : this.$refs.sankeyContainer;
      if (!container) return;

      const svg = container.querySelector('svg');
      if (!svg) return;

      // Get D3 selection and extract data as absolute positions
      const nodeSelection = d3.select(svg).selectAll('g.nodes g');
      nodeSelection.each(d => {
        if (d && d.name) {
          // Validate that positions are valid numbers before saving
          const {x0} = d;
          const {x1} = d;
          const {y0} = d;
          const {y1} = d;

          // Only save if all values are valid numbers
          if (Number.isFinite(x0) && Number.isFinite(x1) &&
              Number.isFinite(y0) && Number.isFinite(y1)) {
            this.nodePositions[d.name] = {
              x0, x1, y0, y1,
            };
          }
        }
      });

      // Save current zoom transform
      const svgSelection = d3.select(svg);
      this.currentZoomTransform = d3.zoomTransform(svgSelection.node());
    },
    openFullscreen() {
      this.saveNodePositions();
      this.isFullscreen = true;
      document.body.style.overflow = 'hidden';
      this.$nextTick(() => {
        if (this.$refs.fullscreenSankeyContainer) {
          this.renderSankeyChart(this.$refs.fullscreenSankeyContainer, true);
        }
      });
    },
    closeFullscreen() {
      this.saveNodePositions();
      this.isFullscreen = false;
      document.body.style.overflow = '';
      this.$nextTick(() => this.renderSankeyChart());
    },
    processCves(cvesToProcess) {
      const cveList = cvesToProcess || this.selectedCves;
      if (!cveList.length) return;

      this.loading = true;
      this.chartNodes = [];
      this.chartLinks = [];
      this.nodePositions = {};

      try {
        let nodes = [];
        let links = [];

        // Use pre-filtered visualization data from parent if available
        if (this.filteredVisualization && this.filteredVisualization.nodes) {
          if (this.filteredVisualization.nodes.length === 0) {
            this.chartNodes = [];
            this.chartLinks = [];
            this.hasProcessed = true;
            this.$nextTick(() => this.renderSankeyChart());
            return;
          }

          nodes = this.filteredVisualization.nodes.map(n => ({ name: n.id }));
          links = this.filteredVisualization.links.map(l => ({
            source: String(l.source),
            target: String(l.target),
            value: l.value || 1,
          }));
        } else {
          // Use raw visualization data from backend (via effectiveAssetCorrelation)
          const visualization = this.effectiveAssetCorrelation?.visualization;

          if (visualization && visualization.nodes && visualization.nodes.length > 0) {
            nodes = visualization.nodes.map(n => ({ name: n.id }));
            links = visualization.links.map(l => ({
              source: String(l.source),
              target: String(l.target),
              value: l.value || 1,
            }));
          } else {
            // No visualization data available from backend
            this.chartNodes = [];
            this.chartLinks = [];
            this.hasProcessed = true;
            this.$nextTick(() => this.renderSankeyChart());
            return;
          }
        }

        // Apply path mode filter
        const filtered = this.applyPathModeFilter(nodes, links);
        this.chartNodes = filtered.nodes;
        this.chartLinks = filtered.links;

        this.hasProcessed = true;
        this.$nextTick(() => this.renderSankeyChart());
      } catch (err) {
        console.error('❌ Error in processCves:', err);
      } finally {
        this.loading = false;
      }
    },
    renderSankeyChart(containerOverride, centerInFullscreen = false) {
      const container = containerOverride || this.$refs.sankeyContainer;
      if (!container || !this.chartNodes.length) return;

      // Validate chartNodes - ensure all nodes have valid names
      const validChartNodes = this.chartNodes.filter(n => n && n.name && typeof n.name === 'string');
      if (validChartNodes.length === 0) {
        return;
      }

      // Create TRUE deep copies using JSON to prevent any mutation of original data
      // This is critical because d3-sankey mutates links in place
      let linksCopy;
      try {
        linksCopy = JSON.parse(JSON.stringify(this.chartLinks));
      } catch (e) {
        return;
      }

      // Validate links - ensure all have string source/target
      const validChartLinks = linksCopy.filter(l => {
        if (!l) return false;
        return l.source && l.target && typeof l.source === 'string' && typeof l.target === 'string';
      });

      if (validChartLinks.length === 0) {
        return;
      }

      // Validate that we have valid links with existing node references
      const nodeNames = new Set(validChartNodes.map(n => n.name));
      const validLinks = validChartLinks.filter(
        l => nodeNames.has(l.source) && nodeNames.has(l.target),
      );

      // Don't render if no valid links exist
      if (validLinks.length === 0) {
        return;
      }

      this.selectedPath = null;
      d3.select(container).selectAll('*').remove();

      const { margin } = LAYOUT_CONFIG;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      const svgRoot = d3.select(container)
        .append('svg')
        .attr('width', '100%')
        .attr('height', '100%');

      svgRoot.append('rect')
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('fill', 'transparent')
        .style('cursor', 'move');

      const svg = svgRoot.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const zoomBehavior = d3.zoom()
        .scaleExtent([0.2, 4])
        .on('zoom', event => { svg.attr('transform', event.transform); });
      svgRoot.call(zoomBehavior);

      // Restore saved transform if available (centering will be done after render)
      if (!centerInFullscreen && this.currentZoomTransform) {
        svgRoot.call(zoomBehavior.transform, this.currentZoomTransform);
      }

      // Only include nodes that have at least one connection
      const linkedNodeNames = new Set();
      validLinks.forEach(l => {
        linkedNodeNames.add(l.source);
        linkedNodeNames.add(l.target);
      });
      const nodesWithLinks = validChartNodes.filter(n => linkedNodeNames.has(n.name));

      // Ensure we have at least 2 nodes for a valid sankey
      if (nodesWithLinks.length < 2) {
        return;
      }

      // Filter out any self-referencing links
      const cleanLinks = validLinks.filter(l => l.source !== l.target);
      if (cleanLinks.length === 0) {
        return;
      }

      // Create a set of node names that will be in the sankey
      const nodesWithLinksNames = new Set(nodesWithLinks.map(n => n.name));

      // Filter links to only include those where both source and target are in nodesWithLinks
      const finalLinks = cleanLinks.filter(
        l => nodesWithLinksNames.has(l.source) && nodesWithLinksNames.has(l.target),
      );

      if (finalLinks.length === 0) {
        return;
      }

      const sankeyData = {
        nodes: nodesWithLinks.map(n => ({ name: n.name })),
        links: finalLinks.map(l => ({ source: l.source, target: l.target, value: l.value })),
      };

      const sankeyGenerator = createSankeyGenerator(this.flowSettings, sankeyData.nodes, containerWidth, containerHeight);

      // Validate that all link sources and targets exist in nodes
      const sankeyNodeNameSet = new Set(sankeyData.nodes.map(n => n.name));
      const sankeyInvalidLinks = sankeyData.links.filter(l => !sankeyNodeNameSet.has(l.source) || !sankeyNodeNameSet.has(l.target));
      if (sankeyInvalidLinks.length > 0) {
        // Remove invalid links silently
        sankeyData.links = sankeyData.links.filter(l => sankeyNodeNameSet.has(l.source) && sankeyNodeNameSet.has(l.target));
      }

      // Check for isolated nodes (nodes with no links)
      const sankeyNodesWithLinks = new Set();
      sankeyData.links.forEach(l => {
        sankeyNodesWithLinks.add(l.source);
        sankeyNodesWithLinks.add(l.target);
      });
      const isolatedNodes = sankeyData.nodes.filter(n => !sankeyNodesWithLinks.has(n.name));
      if (isolatedNodes.length > 0) {
        // Remove isolated nodes as they can cause sankey layout issues
        sankeyData.nodes = sankeyData.nodes.filter(n => sankeyNodesWithLinks.has(n.name));
      }

      let nodes;
      let links;
      try {
        // Validate that all link sources and targets exist in nodes
        const nodeNameSet = new Set(sankeyData.nodes.map(n => n.name));
        const invalidLinks = sankeyData.links.filter(
          l => !nodeNameSet.has(l.source) || !nodeNameSet.has(l.target),
        );
        if (invalidLinks.length > 0) {
          // Remove invalid links
          sankeyData.links = sankeyData.links.filter(
            l => nodeNameSet.has(l.source) && nodeNameSet.has(l.target),
          );
        }

        // Check for circular links (same source and target)
        const circularLinks = sankeyData.links.filter(l => l.source === l.target);
        if (circularLinks.length > 0) {
          sankeyData.links = sankeyData.links.filter(l => l.source !== l.target);
        }

        // Check for duplicate links
        const linkKeys = new Set();
        sankeyData.links = sankeyData.links.filter(l => {
          const key = `${l.source}|${l.target}`;
          if (linkKeys.has(key)) {
            return false;
          }
          linkKeys.add(key);
          return true;
        });

        // Remove orphan nodes (nodes with no links after all filtering)
        const linkedNodesAfterFilter = new Set();
        sankeyData.links.forEach(l => {
          linkedNodesAfterFilter.add(l.source);
          linkedNodesAfterFilter.add(l.target);
        });
        const orphanNodes = sankeyData.nodes.filter(n => !linkedNodesAfterFilter.has(n.name));
        if (orphanNodes.length > 0) {
          sankeyData.nodes = sankeyData.nodes.filter(n => linkedNodesAfterFilter.has(n.name));
        }

        // Detect and remove cycles - d3-sankey cannot handle cycles
        const removeCycles = (inputNodes, inputLinks) => {
          const adjList = new Map();
          inputNodes.forEach(n => adjList.set(n.name, []));
          inputLinks.forEach(l => {
            if (adjList.has(l.source)) {
              adjList.get(l.source).push(l.target);
            }
          });

          const visited = new Set();
          const recStack = new Set();
          const cycleEdges = [];

          const detectCycle = (node, path) => {
            visited.add(node);
            recStack.add(node);

            const neighbors = adjList.get(node) || [];
            let foundCycle = false;
            neighbors.forEach(neighbor => {
              if (!visited.has(neighbor)) {
                if (detectCycle(neighbor, [...path, node])) {
                  foundCycle = true;
                }
              } else if (recStack.has(neighbor)) {
                // Found a cycle - mark the back edge for removal
                cycleEdges.push({ source: node, target: neighbor });
              }
            });

            recStack.delete(node);
            return foundCycle;
          };

          inputNodes.forEach(n => {
            if (!visited.has(n.name)) {
              detectCycle(n.name, []);
            }
          });

          if (cycleEdges.length > 0) {
            const cycleEdgeSet = new Set(cycleEdges.map(e => `${e.source}|${e.target}`));
            return inputLinks.filter(l => !cycleEdgeSet.has(`${l.source}|${l.target}`));
          }
          return inputLinks;
        };

        sankeyData.links = removeCycles(sankeyData.nodes, sankeyData.links);

        // After cycle removal, remove any new orphan nodes
        const linkedAfterCycleRemoval = new Set();
        sankeyData.links.forEach(l => {
          linkedAfterCycleRemoval.add(l.source);
          linkedAfterCycleRemoval.add(l.target);
        });
        sankeyData.nodes = sankeyData.nodes.filter(n => linkedAfterCycleRemoval.has(n.name));

        // Validate node names have proper prefixes for column assignment (case-insensitive)
        const isValidNodeName = name => {
          if (!name) return false;
          const nameLower = name.toLowerCase();
          return nameLower.startsWith('org:') ||
                 nameLower.startsWith('asset:') ||
                 nameLower.startsWith('tech:') ||
                 name.startsWith('CVE-') ||
                 name.startsWith('CWE-') ||
                 name.startsWith('CAPEC-') ||
                 /^T\d+/.test(name) || // TTP
                 nameLower.startsWith('apt:');
        };

        const invalidNodes = sankeyData.nodes.filter(n => !isValidNodeName(n.name));
        if (invalidNodes.length > 0) {
          const validNodeNames = new Set(sankeyData.nodes.filter(n => isValidNodeName(n.name)).map(n => n.name));
          sankeyData.nodes = sankeyData.nodes.filter(n => isValidNodeName(n.name));
          sankeyData.links = sankeyData.links.filter(l => validNodeNames.has(l.source) && validNodeNames.has(l.target));
        }

        // Final validation before sankey
        if (sankeyData.nodes.length < 2 || sankeyData.links.length === 0) {
          return;
        }

        const result = sankeyGenerator(sankeyData);
        nodes = result.nodes;
        links = result.links;

        // Reassign nodes to correct logical columns (ORG=0, ASSET=1, TECH=2, CVE=3, CWE=4, CAPEC=5, TTP=6, APT=7)
        reassignNodesToLogicalColumns(nodes, containerWidth);
        // Update link positions after reassigning columns
        updateLinkPositions(links);
      } catch (err) {
        // Sankey layout failed - log and return
        console.error('Sankey layout failed:', err);
        return;
      }

      // Validate sankey output
      if (!nodes || !links || nodes.length === 0) {
        return;
      }

      // Restore saved positions if available and valid
      const savedPositions = this.nodePositions;
      nodes.forEach(node => {
        if (savedPositions[node.name]) {
          const saved = savedPositions[node.name];
          // Validate saved positions before applying
          if (Number.isFinite(saved.x0) && Number.isFinite(saved.x1) &&
              Number.isFinite(saved.y0) && Number.isFinite(saved.y1)) {
            Object.assign(node, {
              x0: saved.x0,
              x1: saved.x1,
              y0: saved.y0,
              y1: saved.y1,
            });
          }
        }
      });

      const tooltip = d3.select(container)
        .append('div')
        .attr('class', 'sankey-tooltip')
        .style('position', 'absolute')
        .style('visibility', 'hidden')
        .style('background', 'rgba(0,0,0,0.8)')
        .style('color', '#fff')
        .style('padding', '8px 12px')
        .style('border-radius', '4px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('z-index', '100');

      // Draw links using standard Sankey visualization
      const linkOpacity = (this.flowSettings.linkOpacity || 35) / 100;
      const linkElements = svg.append('g')
        .attr('class', 'links')
        .selectAll('path')
        .data(links)
        .join('path')
        .attr('d', generateGradientLinkPath)
        .attr('stroke', d => getNodeColor(d.source.name))
        .attr('stroke-opacity', linkOpacity)
        .attr('stroke-width', d => Math.max(1, d.width))
        .attr('fill', 'none')
        .style('cursor', 'pointer');

      // Draw nodes
      const nodeGroups = svg.append('g')
        .attr('class', 'nodes')
        .selectAll('g')
        .data(nodes)
        .join('g')
        .style('cursor', 'grab');

      // Drag behavior
      const self = this;
      const dragBehavior = d3.drag()
        .subject((event, d) => ({ x: d.x0, y: d.y0 }))
        .on('start', function onDragStart() { d3.select(this).style('cursor', 'grabbing'); })
        .on('drag', function onDrag(event, d) {
          const nodeHeight = d.y1 - d.y0;
          const nodeWidth = d.x1 - d.x0;
          const newX = event.x;
          const newY = event.y;
          Object.assign(d, {
            x0: newX, x1: newX + nodeWidth, y0: newY, y1: newY + nodeHeight,
          });
          d3.select(this).select('rect').attr('x', d.x0).attr('y', d.y0);
          const textAnchor = d.x0 < LAYOUT_CONFIG.width / 2 ? 'start' : 'end';
          const textX = d.x0 < LAYOUT_CONFIG.width / 2 ? d.x1 + 6 : d.x0 - 6;
          d3.select(this).select('text').attr('x', textX).attr('y', (d.y1 + d.y0) / 2)
            .attr('text-anchor', textAnchor);
          updateLinkPositions(links);
          linkElements.attr('d', generateGradientLinkPath);
        })
        .on('end', function onDragEnd() { d3.select(this).style('cursor', 'grab'); });

      nodeGroups.call(dragBehavior);

      // Node rectangles
      const nodeRects = nodeGroups.append('rect')
        .attr('x', d => d.x0)
        .attr('y', d => d.y0)
        .attr('height', d => Math.max(1, d.y1 - d.y0))
        .attr('width', d => d.x1 - d.x0)
        .attr('fill', d => getNodeColor(d.name))
        .attr('opacity', 1)
        .attr('rx', 2)
        .attr('ry', 2);

      // Helper function to get display name (strip prefixes)
      const getDisplayName = name => {
        if (!name) return '';
        const nameLower = name.toLowerCase();
        if (nameLower.startsWith('org:')) return name.substring(4);
        if (nameLower.startsWith('asset:')) return name.substring(6);
        if (nameLower.startsWith('tech:')) return name.substring(5);
        if (nameLower.startsWith('apt:')) return name.substring(4);
        return name;
      };

      // Node labels - use CSS variable for theme-aware color
      const style = getComputedStyle(document.documentElement);
      const labelColor = style.getPropertyValue('--graph-text').trim() || '#333';
      const labelSize = `${this.flowSettings.labelSize || 11}px`;
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      const labelBgColor = isDark ? 'rgba(30, 32, 40, 0.85)' : 'rgba(255, 255, 255, 0.85)';

      // Add background rects for labels (inserted before text so they render behind)
      nodeGroups.append('rect')
        .attr('class', 'label-bg')
        .attr('fill', labelBgColor)
        .attr('rx', 3)
        .attr('ry', 3)
        .style('pointer-events', 'none');

      const textLabels = nodeGroups.append('text')
        .attr('x', d => (d.x0 < LAYOUT_CONFIG.width / 2 ? d.x1 + 6 : d.x0 - 6))
        .attr('y', d => (d.y1 + d.y0) / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', d => (d.x0 < LAYOUT_CONFIG.width / 2 ? 'start' : 'end'))
        .attr('font-size', labelSize)
        .attr('fill', labelColor)
        .text(d => getDisplayName(d.name));

      // Size background rects to match text bounding boxes
      textLabels.each(function () {
        const textEl = this;
        const bbox = textEl.getBBox();
        const padding = 2;
        const bg = d3.select(textEl.parentNode).select('.label-bg');
        bg.attr('x', bbox.x - padding)
          .attr('y', bbox.y - padding)
          .attr('width', bbox.width + padding * 2)
          .attr('height', bbox.height + padding * 2);
      });

      // Node hover handlers
      nodeRects
        .on('mouseover', (event, d) => {
          if (self.isAnimating) return;
          // Always show hover effect, even if there's a selection
          const { connectedNodes, connectedLinks } = getConnectedElements(d.name, links);
          nodeRects.attr('opacity', n => {
            if (n.name === d.name) return 1;
            if (connectedNodes.has(n.name)) return 0.8;
            return 0.3;
          });
          linkElements.attr('stroke-opacity', (l, i) => (connectedLinks.has(i) ? 0.5 : 0.1));
          tooltip.style('visibility', 'visible').html(`<b>${getDisplayName(d.name)}</b>`);
        })
        .on('mousemove', event => {
          tooltip.style('top', `${event.offsetY - 10}px`).style('left', `${event.offsetX + 10}px`);
        })
        .on('mouseout', () => {
          if (self.isAnimating) return;
          tooltip.style('visibility', 'hidden');
          // If there's a selection, restore it; otherwise reset to neutral
          if (self.selectedPath) {
            const { connectedNodes, connectedLinks } = self.selectedPath;
            nodeRects.attr('opacity', n => {
              if (n.name === self.selectedPath.name) return 1;
              if (connectedNodes.has(n.name)) return 0.8;
              return 0.2;
            });
            linkElements.attr('stroke-opacity', (l, i) => (connectedLinks.has(i) ? 0.6 : 0.05));
          } else {
            nodeRects.attr('opacity', 1);
            linkElements.attr('stroke-opacity', linkOpacity);
          }
        })
        .on('click', (event, d) => {
          event.stopPropagation();
          // If clicking the same node, toggle off
          if (self.selectedPath && self.selectedPath.name === d.name) {
            self.selectedPath = null;
            self.selectedPathInfo = null;
            nodeRects.attr('opacity', 1);
            linkElements.attr('stroke-opacity', linkOpacity);
            tooltip.style('visibility', 'hidden');
            return;
          }
          const { connectedNodes, connectedLinks } = getConnectedElements(d.name, links);
          self.selectedPath = {
            type: 'node', name: d.name, connectedNodes, connectedLinks,
          };
          nodeRects.attr('opacity', n => {
            if (n.name === d.name) return 1;
            if (connectedNodes.has(n.name)) return 0.8;
            return 0.2;
          });
          linkElements.attr('stroke-opacity', (l, i) => (connectedLinks.has(i) ? 0.6 : 0.05));
          tooltip.style('visibility', 'hidden');
          const cves = [];
          const cwes = [];
          const capecs = [];
          const techniques = [];
          connectedNodes.forEach(name => {
            if (name.startsWith('CVE-') || name.startsWith('EXPOSED-') || name.startsWith('ASSET-')) {
              cves.push(name);
            } else if (name.startsWith('CWE-')) {
              cwes.push(name);
            } else if (name.startsWith('CAPEC-')) {
              capecs.push(name);
            } else if (/^T\d+/.test(name)) {
              techniques.push(name);
            }
          });
          self.selectedPathInfo = {
            name: getDisplayName(d.name),
            type: self.getNodeType(d.name),
            cves,
            cwes,
            capecs,
            techniques,
            connectedNodes: Array.from(connectedNodes),
          };
        });

      // Link hover handlers
      linkElements
        .on('mouseover', (event, d) => {
          if (self.isAnimating) return;
          // Always show hover effect, even if there's a selection
          const connectedLinksSet = new Set();
          const connectedNodesSet = new Set([d.source.name, d.target.name]);
          links.forEach((link, i) => { if (link === d) connectedLinksSet.add(i); });
          nodeRects.attr('opacity', n => (connectedNodesSet.has(n.name) ? 1 : 0.25));
          linkElements.attr('stroke-opacity', (l, i) => (connectedLinksSet.has(i) ? 0.7 : 0.08));
          tooltip.style('visibility', 'visible').html(`${getDisplayName(d.source.name)} → ${getDisplayName(d.target.name)}`);
        })
        .on('mousemove', event => {
          tooltip.style('top', `${event.offsetY - 10}px`).style('left', `${event.offsetX + 10}px`);
        })
        .on('mouseout', () => {
          if (self.isAnimating) return;
          tooltip.style('visibility', 'hidden');
          // If there's a selection, restore it; otherwise reset to neutral
          if (self.selectedPath) {
            const { connectedNodes, connectedLinks } = self.selectedPath;
            nodeRects.attr('opacity', n => {
              if (n.name === self.selectedPath.name) return 1;
              if (connectedNodes.has(n.name)) return 0.8;
              return 0.2;
            });
            linkElements.attr('stroke-opacity', (l, i) => (connectedLinks.has(i) ? 0.6 : 0.05));
          } else {
            nodeRects.attr('opacity', 1);
            linkElements.attr('stroke-opacity', linkOpacity);
          }
        })
        .on('click', (event, d) => {
          event.stopPropagation();
          const connectedNodesSet = new Set([d.source.name, d.target.name]);
          const connectedLinksSet = new Set();
          links.forEach((link, i) => { if (link === d) connectedLinksSet.add(i); });
          self.selectedPath = {
            type: 'link', name: `${getDisplayName(d.source.name)} → ${getDisplayName(d.target.name)}`, connectedNodes: connectedNodesSet, connectedLinks: connectedLinksSet,
          };
          nodeRects.attr('opacity', n => (connectedNodesSet.has(n.name) ? 0.9 : 0.2));
          linkElements.attr('stroke-opacity', (l, i) => (connectedLinksSet.has(i) ? 0.7 : 0.05));
          tooltip.style('visibility', 'hidden');
          const cves = [];
          const cwes = [];
          const capecs = [];
          const techniques = [];
          connectedNodesSet.forEach(name => {
            if (name.startsWith('CVE-')) cves.push(name);
            else if (name.startsWith('CWE-')) cwes.push(name);
            else if (name.startsWith('CAPEC-')) capecs.push(name);
            else if (/^T\d+/.test(name)) techniques.push(name);
          });
          self.selectedPathInfo = {
            name: `${getDisplayName(d.source.name)} → ${getDisplayName(d.target.name)}`, type: 'link', cves, cwes, capecs, techniques, connectedNodes: Array.from(connectedNodesSet),
          };
        });

      // Background click to deselect
      svgRoot.select('rect').on('click', () => {
        if (self.selectedPath) {
          self.selectedPath = null;
          self.selectedPathInfo = null;
          nodeRects.attr('opacity', 1);
          linkElements.attr('stroke-opacity', 0.35);
          tooltip.style('visibility', 'hidden');
        }
      });

      // Center the visualization based on actual content bounds
      if (centerInFullscreen || !this.currentZoomTransform) {
        // Calculate actual bounds of the rendered content
        const contentBounds = svg.node().getBBox();
        const contentWidth = contentBounds.width + contentBounds.x;
        const contentHeight = contentBounds.height + contentBounds.y;

        // Validate bounds before calculating transform
        if (Number.isFinite(contentWidth) && Number.isFinite(contentHeight) &&
            contentWidth > 0 && contentHeight > 0) {
          // Scale only based on HEIGHT to fit vertically, keep width spread
          const padding = 60;
          const availableHeight = containerHeight - padding * 2;
          const scaleY = availableHeight / contentHeight;
          const scale = Math.min(scaleY, 1); // Only scale down if needed, based on height

          // Calculate offset to center horizontally
          const scaledWidth = contentWidth * scale;
          const offsetX = (containerWidth - scaledWidth) / 2;
          const offsetY = padding;

          // Validate final transform values
          if (Number.isFinite(offsetX) && Number.isFinite(offsetY) && Number.isFinite(scale)) {
            const centerTransform = d3.zoomIdentity
              .translate(offsetX, offsetY)
              .scale(scale);
            svgRoot.call(zoomBehavior.transform, centerTransform);
          }
        }
      }

      // Only animate once per mount — skip re-renders from data updates, theme changes, etc.
      if (!this.hasRenderedOnce) {
        this.hasRenderedOnce = true;
        this.animateFlowEntrance(nodeGroups, linkElements, linkOpacity);
      }
    },
    animateFlowEntrance(nodeGroups, linkElements, linkOpacity) {
      // Set animation guard to prevent hover from interrupting
      this.isAnimating = true;

      // Group nodes by their x position (column)
      const nodesByColumn = new Map();
      nodeGroups.each(function collectNodes(d) {
        const col = Math.floor(d.x0 / 100); // Approximate column grouping
        if (!nodesByColumn.has(col)) nodesByColumn.set(col, []);
        nodesByColumn.get(col).push(this);
      });

      // Sort columns left to right
      const sortedColumns = Array.from(nodesByColumn.keys()).sort((a, b) => a - b);

      const delayPerColumn = 150; // ms between columns
      const animationDuration = 500;
      let maxDelay = 0;

      // Animate each column
      sortedColumns.forEach((col, colIndex) => {
        const nodesInColumn = nodesByColumn.get(col);
        const columnDelay = colIndex * delayPerColumn;

        nodesInColumn.forEach((nodeElement, nodeIndex) => {
          const nodeDelay = columnDelay + (nodeIndex * 30); // Stagger within column
          if (nodeDelay + animationDuration > maxDelay) maxDelay = nodeDelay + animationDuration;

          d3.select(nodeElement)
            .attr('opacity', 0)
            .attr('transform', 'translate(-20, 0)') // Start slightly left
            .transition('entrance')
            .delay(nodeDelay)
            .duration(animationDuration)
            .ease(d3.easeBackOut.overshoot(1.2))
            .attr('opacity', 1)
            .attr('transform', 'translate(0, 0)');
        });
      });

      // Animate links fading in after first column appears — named transition
      linkElements
        .attr('stroke-opacity', 0)
        .transition('entrance')
        .delay(delayPerColumn * 1.5)
        .duration(700)
        .attr('stroke-opacity', linkOpacity);

      // Clear animation guard after all transitions complete
      const totalDuration = Math.max(maxDelay, delayPerColumn * 1.5 + 700);
      setTimeout(() => { this.isAnimating = false; }, totalDuration);
    },
    exportData(format = 'json') {
      const data = {
        cves: this.selectedCves,
        nodes: this.chartNodes,
        links: this.chartLinks,
        attackPaths: this.selectedCves.map(cve => this.attackPathsMap[cve]).filter(Boolean),
        generatedAt: new Date().toISOString(),
      };

      if (format === 'html') {
        const svgElement = this.$refs.sankeyContainer?.querySelector('svg');
        const svgContent = prepareSvgForExport(svgElement);
        const html = generateHtmlExport(data, svgContent);
        downloadFile(html, `cve-attack-visualization-${Date.now()}.html`, 'text/html');
      } else {
        downloadFile(
          JSON.stringify(data, null, 2),
          `cve-attack-visualization-${Date.now()}.json`,
          'application/json',
        );
      }
    },

    /**
     * Highlight and center on a specific node by name
     * Called from parent component's search feature
     */
    highlightAndCenterNode(nodeName) {
      const container = this.isFullscreen
        ? this.$refs.fullscreenSankeyContainer
        : this.$refs.sankeyContainer;
      if (!container) return;

      const svg = container.querySelector('svg');
      if (!svg) return;

      const svgSelection = d3.select(svg);
      const nodeGroups = svgSelection.selectAll('g.nodes g');
      const linkElements = svgSelection.selectAll('g.links path');

      // Find the target node
      let targetNode = null;
      nodeGroups.each(d => {
        if (d && d.name === nodeName) {
          targetNode = d;
        }
      });

      if (!targetNode) return;

      // Get connected elements for highlighting
      const links = [];
      linkElements.each(d => {
        links.push(d);
      });
      const { connectedNodes, connectedLinks } = getConnectedElements(nodeName, links);

      // Update selection state
      this.selectedPath = {
        type: 'node',
        name: nodeName,
        connectedNodes,
        connectedLinks,
      };

      // Apply highlighting
      const nodeRects = svgSelection.selectAll('g.nodes rect');

      nodeRects.attr('opacity', n => {
        if (n.name === nodeName) return 1;
        if (connectedNodes.has(n.name)) return 0.8;
        return 0.2;
      });
      linkElements.attr('stroke-opacity', (l, i) => (connectedLinks.has(i) ? 0.6 : 0.05));

      // Center the view on the target node
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const nodeX = (targetNode.x0 + targetNode.x1) / 2;
      const nodeY = (targetNode.y0 + targetNode.y1) / 2;

      const scale = 1.2;
      const translateX = containerWidth / 2 - nodeX * scale;
      const translateY = containerHeight / 2 - nodeY * scale;

      const zoomBehavior = d3.zoom().scaleExtent([0.2, 4]);
      const transform = d3.zoomIdentity
        .translate(translateX, translateY)
        .scale(scale);

      svgSelection
        .transition()
        .duration(500)
        .call(zoomBehavior.transform, transform);

      // Pulse effect on the target node
      nodeGroups.filter(d => d.name === nodeName)
        .select('rect')
        .transition()
        .duration(200)
        .attr('stroke', '#fff')
        .attr('stroke-width', 3)
        .transition()
        .duration(200)
        .attr('stroke-width', 1)
        .transition()
        .duration(200)
        .attr('stroke-width', 3)
        .transition()
        .duration(200)
        .attr('stroke-width', 0);

      // Build info panel data
      const getDisplayName = name => {
        if (!name) return '';
        const nameLower = name.toLowerCase();
        if (nameLower.startsWith('org:')) return name.substring(4);
        if (nameLower.startsWith('asset:')) return name.substring(6);
        if (nameLower.startsWith('tech:')) return name.substring(5);
        if (nameLower.startsWith('apt:')) return name.substring(4);
        return name;
      };

      const cves = [];
      const cwes = [];
      const capecs = [];
      const techniques = [];
      connectedNodes.forEach(name => {
        if (name.startsWith('CVE-')) cves.push(name);
        else if (name.startsWith('CWE-')) cwes.push(name);
        else if (name.startsWith('CAPEC-')) capecs.push(name);
        else if (/^T\d+/.test(name)) techniques.push(name);
      });

      this.selectedPathInfo = {
        name: getDisplayName(nodeName),
        type: this.getNodeType(nodeName),
        cves,
        cwes,
        capecs,
        techniques,
        connectedNodes: Array.from(connectedNodes),
      };
    },

    /**
     * Clear highlight and reset view
     */
    clearHighlight() {
      const container = this.isFullscreen
        ? this.$refs.fullscreenSankeyContainer
        : this.$refs.sankeyContainer;
      if (!container) return;

      const svg = container.querySelector('svg');
      if (!svg) return;

      const svgSelection = d3.select(svg);
      const nodeRects = svgSelection.selectAll('g.nodes rect');
      const linkElements = svgSelection.selectAll('g.links path');
      const linkOpacity = (this.flowSettings.linkOpacity || 35) / 100;

      // Reset highlighting
      this.selectedPath = null;
      this.selectedPathInfo = null;
      nodeRects.attr('opacity', 1).attr('stroke-width', 0);
      linkElements.attr('stroke-opacity', linkOpacity);
    },

    /**
     * Get all searchable nodes for the search feature
     */
    getSearchableNodes() {
      return this.chartNodes.map(n => {
        const {name} = n;
        let type = 'unknown';
        let label = name;
        const nameLower = name.toLowerCase();

        if (nameLower.startsWith('org:')) {
          type = 'org';
          label = name.substring(4);
        } else if (nameLower.startsWith('asset:')) {
          type = 'asset';
          label = name.substring(6);
        } else if (nameLower.startsWith('tech:')) {
          type = 'tech';
          label = name.substring(5);
        } else if (name.startsWith('CVE-')) {
          type = 'cve';
          label = name;
        } else if (name.startsWith('CWE-')) {
          type = 'cwe';
          label = name;
        } else if (name.startsWith('CAPEC-')) {
          type = 'capec';
          label = name;
        } else if (/^T\d+/.test(name)) {
          type = 'ttp';
          label = name;
        } else if (nameLower.startsWith('apt:')) {
          type = 'apt';
          label = name.substring(4);
        }

        return { id: name, type, label };
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.cve-attack-visualizer {
  background: var(--bg-widget);
  border-radius: 8px;
  padding: 20px;
  margin-top: 24px;
  position: relative;

  &.dark-theme {
    background: var(--bg-secondary);
  }
}

.visualization-area {
  height: 600px;
  background: var(--graph-bg);
  overflow: hidden;
}

.visualizer-loading,
.visualizer-no-data,
.visualizer-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  color: var(--text-muted);

  .no-data-text,
  .empty-text {
    margin-top: 12px;
    font-size: 14px;
  }
}

// Full-screen loading overlay (positioned within content area)
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px 48px;
  background: var(--bg-widget);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.loading-text {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

.visualizer-content-wrapper {
  display: flex;
  height: 100%;
}

.visualizer-content {
  flex: 1;
  height: 100%;
  overflow: hidden;
  transition: flex 0.3s ease;
}

.sankey-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.info-panel {
  height: 100%;
  background: var(--graph-panel-bg);
  border-left: 1px solid var(--border-primary);
  overflow-y: auto;
  flex-shrink: 0;
}

.slide-panel-enter-active,
.slide-panel-leave-active {
  transition: all 0.3s ease;
}

.slide-panel-enter,
.slide-panel-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

// Fullscreen styles
.fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-primary);
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.fullscreen-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.fullscreen-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: var(--bg-widget);
  border-bottom: 1px solid var(--border-primary);
  flex-shrink: 0;

  &__left {
    .visualizer-title {
      font-size: 18px;
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: 4px;
    }

    .visualizer-subtitle {
      font-size: 13px;
      color: var(--text-secondary);
    }
  }

  .close-fullscreen-btn {
    background: var(--bg-secondary);

    &:hover {
      background: var(--bg-tertiary);
    }
  }
}

.fullscreen-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.fullscreen-visualization {
  flex: 1;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.fullscreen-info-panel {
  max-height: 100%;
  border-left: 1px solid var(--border-primary);
}

// Embedded mode styles
.embedded-mode {
  margin-top: 0;
  padding: 0;
  height: 100%;

  .visualization-area {
    height: 100%;
  }
}

// Dark theme styles - now handled by CSS variables
.dark-theme {
  .visualization-area {
    background: var(--graph-bg);
  }
}
</style>
